// src/services/siteConfigService.js
import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';

export const SITE_BASE = 'https://elionsociety.com.br';

export function normalizeHandle(raw = '') {
  return String(raw)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '-') // troca tudo por hífen
    .replace(/-+/g, '-') // remove hífen repetido
    .replace(/^-|-$/g, ''); // remove hífen nas pontas
}

export function buildSiteUrl(handle = '') {
  const h = normalizeHandle(handle);
  return h ? `${SITE_BASE}/u/${h}` : `${SITE_BASE}/u/seu-nome`;
}

export function buildWaLink(whatsDisplay = '') {
  const digits = String(whatsDisplay).replace(/\D/g, '');
  if (!digits) return '';
  const withCountry = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}`;
}

export async function loadUserDoc(uid) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/** Checa se handle está livre (client-side)
 * ✅ Nunca lança exception: sempre retorna { ok, reason? }
 */
export async function checkHandleAvailable(handle, uid) {
  try {
    const h = normalizeHandle(handle);

    if (!h || h.length < 3) {
      return {
        ok: false,
        reason: 'Escolha um link válido (mínimo 3 caracteres).',
      };
    }

    const href = doc(db, 'siteHandles', h);
    const hsnap = await getDoc(href);

    if (!hsnap.exists()) return { ok: true };

    const ownerUid = hsnap.data()?.uid;
    if (ownerUid && ownerUid !== uid) {
      return { ok: false, reason: 'Esse link já está em uso. Tente outro.' };
    }

    // existe, mas é do próprio usuário
    return { ok: true };
  } catch (e) {
    // ✅ falha de rede/regras/permissão/etc
    return {
      ok: false,
      reason: 'Erro ao verificar link. Vamos tentar salvar mesmo assim.',
      error: e?.message || String(e),
    };
  }
}

/**
 * Reserva handle de forma atômica:
 * - cria/garante siteHandles/{handle} -> uid
 * - atualiza users/{uid}.siteHandle
 * - se mudou handle, libera o antigo (se era dele)
 *
 * ⚠️ Aqui pode lançar erro (fonte da verdade)
 */
export async function claimHandle(uid, desiredHandle) {
  const handle = normalizeHandle(desiredHandle);

  if (!handle || handle.length < 3) {
    throw new Error(
      'Defina um link válido (mínimo 3 caracteres, sem espaços).'
    );
  }

  const userRef = doc(db, 'users', uid);
  const newHandleRef = doc(db, 'siteHandles', handle);

  await runTransaction(db, async (tx) => {
    const userSnap = await tx.get(userRef);
    if (!userSnap.exists()) throw new Error('Usuário não encontrado.');

    const oldHandle = userSnap.data()?.siteHandle || '';

    const newHandleSnap = await tx.get(newHandleRef);
    if (newHandleSnap.exists() && newHandleSnap.data()?.uid !== uid) {
      throw new Error('Esse link já está em uso. Tente outro.');
    }

    // libera o antigo
    if (oldHandle && oldHandle !== handle) {
      const oldRef = doc(db, 'siteHandles', oldHandle);
      const oldSnap = await tx.get(oldRef);
      if (oldSnap.exists() && oldSnap.data()?.uid === uid) {
        tx.delete(oldRef);
      }
    }

    // reserva o novo
    const baseData = {
      uid,
      updatedAt: serverTimestamp(),
    };

    // ✅ createdAt só na criação (não sobrescreve)
    if (!newHandleSnap.exists()) {
      tx.set(
        newHandleRef,
        { ...baseData, createdAt: serverTimestamp() },
        { merge: true }
      );
    } else {
      tx.set(newHandleRef, baseData, { merge: true });
    }

    // grava no user
    tx.set(
      userRef,
      { siteHandle: handle, updatedAt: serverTimestamp() },
      { merge: true }
    );
  });

  return handle;
}

/**
 * Salva todos os campos do Meu Site em users/{uid} (merge)
 * Observação: na ETAPA 04 vamos separar meta vs builder, mas isso já serve.
 */
export async function saveSiteConfig(uid, payload) {
  const ref = doc(db, 'users', uid);
  await setDoc(
    ref,
    { ...payload, updatedAt: serverTimestamp() },
    { merge: true }
  );
}
